import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface MenuWrapperProps<OptionType extends CascaderOption>
  extends BoxProps {
  menuRef?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalMenuWrapper = <OptionType extends CascaderOption>({
  menuRef,
  children,
}: {
  menuRef?: React.Ref<HTMLDivElement>;
  /** The children to be rendered. */
  children: React.ReactNode;
}) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomMenuWrapper = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.MENU_WRAPPER),
    [getComponent],
  );

  return (
    <CustomMenuWrapper
      menuRef={menuRef}
      getCommonProps={getCommonProps}
      __css={cascaderStyles.menuWrapper}
      className="cascader-menu-wrapper"
    >
      {children}
    </CustomMenuWrapper>
  );
};

export const MenuWrapper = <OptionType extends CascaderOption>({
  menuRef,
  children,
  getCommonProps: _,
  ...rest
}: MenuWrapperProps<OptionType>) => {
  return (
    <Box ref={menuRef} {...rest}>
      {children}
    </Box>
  );
};
